import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useTenantConfig } from '../contexts/TenantConfigContext';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--background-color);
  padding: 20px;
`;

const Content = styled.div`
  background: var(--surface-color);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-lg);
  padding: 40px;
  max-width: 500px;
  width: 100%;
  text-align: center;
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

const Logo = styled.img`
  width: 100%;
  height: 60px;
  object-fit: contain;
  border-radius: var(--border-radius-md);
  opacity: 0.8;
`;

const Icon = styled.div`
  width: 80px;
  height: 80px;
  background-color: var(--error-light);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  
  svg {
    width: 40px;
    height: 40px;
    color: var(--error-color);
  }
`;

const Title = styled.h1`
  font-size: 24px;
  color: var(--text-primary);
  margin-bottom: 16px;
`;

const Message = styled.p`
  font-size: 16px;
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 32px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-direction: column;
  
  @media (min-width: 480px) {
    flex-direction: row;
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: var(--border-radius-md);
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  &.primary {
    background-color: var(--primary-color);
    color: white;
    
    &:hover {
      background-color: var(--primary-hover);
    }
  }
  
  &.secondary {
    background-color: transparent;
    color: var(--primary-color);
    border: 1px solid var(--primary-color);
    
    &:hover {
      background-color: var(--primary-light);
    }
  }
`;

const UnauthorizedPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  // טעינת הגדרות החברה (per-tenant) - לוגו נטען מ-SystemParameters
  const { config } = useTenantConfig();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getPermissionMessage = () => {
    if (!user) return 'אינך מחובר למערכת';
    
    switch (user.permissionLevel) {
      case 2: // Sales Rep
        return 'אין לך הרשאה לגשת לדף זה. דף זה זמין למנהלי מערכת בלבד.';
      case 3: // Case Manager
        return 'אין לך הרשאה לגשת לדף זה. דף זה זמין למנהלי מערכת בלבד.';
      default:
        return 'אין לך הרשאה לגשת לדף זה.';
    }
  };

  return (
    <Container>
      <Content>
        <LogoContainer>
          <Logo
            src={config?.logoFilename ? `/${config.logoFilename}` : '/logobizchut.png'}
            alt={config?.companyNameHebrew || 'לוגו'}
            loading="lazy"
          />
        </LogoContainer>
        
        <Icon>
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
            />
          </svg>
        </Icon>
        
        <Title>גישה נדחתה</Title>
        <Message>{getPermissionMessage()}</Message>
        
        <ButtonGroup>
          <Button 
            className="primary" 
            onClick={() => navigate('/dashboard')}
          >
            חזרה לדף הבית
          </Button>
          <Button 
            className="secondary" 
            onClick={handleLogout}
          >
            יציאה מהמערכת
          </Button>
        </ButtonGroup>
      </Content>
    </Container>
  );
};

export default UnauthorizedPage;